import { H2, P, B, UL, LI, Steps, Step, Callout, Table } from "./ui";
import { CosteVideo } from "./precios";

export const UnClic = () => (
  <>
    <P>
      La producción en 1 clic hace el vídeo entero: escribe el guion, lo narra, decide qué se ve en
      cada momento, busca ese material, planifica el montaje y renderiza. Tú solo pones el título y
      apruebas el material a mitad de camino.
    </P>

    <H2 id="fases">Las fases, por dentro</H2>
    <Steps>
      <Step n="1" title="Guion">
        Se escribe un guion documental dividido en escenas, con su gancho inicial y su cierre. Cada
        escena lleva además un <B>storyboard</B>: qué se ve en pantalla en cada plano, plano a plano.
      </Step>
      <Step n="2" title="Narración">
        Se sintetiza la voz elegida. Aquí es donde el vídeo adquiere su duración real, que casi
        nunca coincide con la estimada: la voz manda.
      </Step>
      <Step n="3" title="Material">
        Para cada plano del storyboard se busca un vídeo o una foto que muestre lo que describe. Es
        la fase más larga y la que más varía según el tema.
      </Step>
      <Step n="4" title="Revisión">
        El vídeo se para y te enseña lo encontrado. Nada se renderiza hasta que apruebes.
      </Step>
      <Step n="5" title="Montaje y render">
        Se decide dónde van los rótulos, las transiciones y los movimientos de cámara, y se monta
        el archivo final.
      </Step>
    </Steps>

    <H2 id="titulo">El título es la decisión importante</H2>
    <P>
      De él sale todo lo demás. Un título concreto produce un guion concreto y búsquedas de material
      que aciertan; uno vago produce un guion de relleno y material genérico.
    </P>
    <Table
      head={["En vez de esto", "Escribe esto"]}
      rows={[
        ["Historias de la Segunda Guerra Mundial", "El día que un submarino alemán se rindió en Argentina"],
        ["Grandes misterios", "5 barcos que aparecieron vacíos en alta mar"],
        ["Vidas de artistas", "Los últimos 3 años de Van Gogh, contados por sus cartas"],
      ]}
    />
    <Callout tipo="truco" titulo="Los números y los nombres propios ayudan dos veces">
      Ayudan al espectador a saber qué va a ver, y ayudan al buscador de material: un nombre propio
      tiene fotografías de archivo asociadas, «grandes misterios» no tiene ninguna.
    </Callout>

    <H2 id="duracion">Elegir la duración</H2>
    <P>
      La duración que pides es un objetivo, no una garantía: el guion se escribe para acercarse a
      ella y la voz real ajusta el resultado. Un vídeo de 20 minutos consume del orden de{" "}
      <CosteVideo minutos={20} />.
    </P>
    <P>
      Cada plan tiene un tope de duración por vídeo. No es un límite de calidad sino de consumo: un
      vídeo más largo gasta proporcionalmente más créditos.
    </P>
  </>
);

export const Revision = () => (
  <>
    <P>
      Cuando el material está listo, el vídeo se detiene y te muestra cada escena con sus clips en
      orden. Esta parada es obligatoria y no se puede desactivar.
    </P>

    <H2 id="por-que">Por qué es obligatoria</H2>
    <P>
      Porque revisar es gratis y renderizar no. Detectar aquí que una escena tiene material que no
      pega cuesta cero; detectarlo en el vídeo terminado significa haber pagado el render para nada
      y volver a pagarlo tras corregir.
    </P>
    <P>
      Antes existía una opción de «producción sin paradas» que saltaba este momento. Se quitó
      precisamente porque invitaba a gastar el render sobre material que nadie había mirado.
    </P>

    <H2 id="que-mirar">Qué mirar, y en qué orden</H2>
    <UL>
      <LI>
        <B>Las primeras escenas, con lupa.</B> Los primeros treinta segundos deciden si alguien se
        queda. Si el material del gancho es flojo, arréglalo aunque el resto esté bien.
      </LI>
      <LI>
        <B>Los clips marcados para revisar.</B> Algunos llevan un aviso porque su licencia no está
        verificada. Si el vídeo es para monetizar, cámbialos.
      </LI>
      <LI>
        <B>Las escenas con muchas tarjetas de texto.</B> Una tarjeta suelta funciona; tres seguidas
        significan que no se encontró material real y se ve pobre.
      </LI>
      <LI>
        <B>Que lo que se ve tenga que ver con lo que se cuenta.</B> Es el fallo más molesto y el más
        fácil de detectar de un vistazo.
      </LI>
    </UL>

    <H2 id="acciones">Qué puedes hacer aquí</H2>
    <Table
      head={["Acción", "Qué hace", "Cuesta créditos"]}
      rows={[
        ["Pulsar sobre un clip", "Lo sustituye por otro, buscando alternativas", "Sí, poco"],
        ["Completar lo que falta", "Busca solo lo que quedó sin cubrir, conserva el resto", "Sí, solo lo que falta"],
        ["Regenerar todo automáticamente", "Tira TODO el material y lo busca de nuevo", "Sí, como una búsqueda entera"],
        ["Volver a renderizar", "Monta el vídeo con el material actual", "Sí, el 10% de la duración"],
      ]}
    />
    <Callout tipo="aviso" titulo="«Regenerar todo» tira también lo que está bien">
      Si solo falla una escena, no uses regenerar todo: pierdes el material correcto de las demás y
      vuelves a pagarlo. Sustituye los clips concretos o usa «Completar lo que falta».
    </Callout>
  </>
);

export const Material = () => (
  <>
    <P>
      El material no se elige al azar ni por palabras sueltas. Al escribir el guion se decide
      también <B>qué se ve en cada plano</B>, y esa descripción es la que se busca.
    </P>

    <H2 id="fuentes">De dónde sale</H2>
    <P>
      Se busca en varias fuentes en cascada, y se acepta la primera que encaja de verdad. El orden
      no es caprichoso: cada fuente es buena para un tipo de plano distinto.
    </P>
    <Table
      head={["Fuente", "Buena para"]}
      rows={[
        ["Pexels, Pixabay, Unsplash", "Metraje y fotografía genérica: paisajes, ambientes, objetos, gente"],
        ["Wikimedia y museos", "Personas y hechos concretos con nombre propio: retratos, documentos, obras"],
        ["Internet Archive", "Metraje histórico y de época"],
        ["Búsqueda web", "Lo que no aparece en las anteriores"],
        ["Imagen generada con IA", "Último recurso, cuando no existe material real de lo que se cuenta"],
      ]}
    />

    <H2 id="verificacion">Se comprueba que pega</H2>
    <P>
      Encontrar un resultado no basta: que una búsqueda devuelva algo no significa que muestre lo
      que hace falta. Antes de aceptar un candidato se comprueba visualmente que corresponde con lo
      que describe la escena. Los que no pasan se descartan y se sigue buscando.
    </P>
    <Callout tipo="info" titulo="Por eso tarda">
      La fase de material es la más larga precisamente por esto. Sale más a cuenta descartar veinte
      candidatos malos que colocar el primero que aparezca.
    </Callout>

    <H2 id="huecos">Cuando no se encuentra nada</H2>
    <P>
      Si un plano se queda sin material real, hay una red de seguridad en tres niveles: se alargan
      los clips de vídeo que ya hay, se busca material más genérico del tema de la escena y, en
      último término, se genera una tarjeta a pantalla completa con la frase que se está narrando
      en ese momento.
    </P>
    <P>
      El objetivo es que nunca haya fotogramas congelados ni pantallas en negro. Pero si ves muchas
      tarjetas, el mensaje es claro: <B>ese tema tiene poco material visual disponible</B>.
    </P>

    <H2 id="temas">Temas que dan buen material, y temas que no</H2>
    <UL>
      <LI><B>Bien:</B> personajes históricos conocidos, sucesos con cobertura de prensa, lugares reales, temas con fotografía de archivo.</LI>
      <LI><B>Regular:</B> conceptos abstractos, sucesos muy recientes, personas poco fotografiadas.</LI>
      <LI><B>Mal:</B> hechos privados sin registro, tecnología muy específica, cualquier cosa ocurrida en la última semana.</LI>
    </UL>
  </>
);

export const Calendario = () => (
  <>
    <P>
      El calendario produce vídeos solo, en las fechas que marques. Es la forma de mantener un canal
      publicando con regularidad sin sentarte a lanzar cada vídeo.
    </P>

    <H2 id="como">Cómo funciona</H2>
    <Steps>
      <Step n="1" title="Generas ideas">
        Se proponen títulos para tu canal a partir de su temática y de lo que funciona en el nicho.
      </Step>
      <Step n="2" title="Los colocas en fechas">
        Cada entrada del calendario es un vídeo con su fecha de creación.
      </Step>
      <Step n="3" title="Se producen solos">
        Llegado el día, el vídeo se crea sin que hagas nada y recibes un correo cuando empieza.
      </Step>
      <Step n="4" title="Los revisas tú">
        Se paran en la revisión, igual que los que lanzas a mano. El calendario automatiza la
        producción, no la decisión de publicar.
      </Step>
    </Steps>

    <Callout tipo="aviso" titulo="El calendario gasta créditos aunque no estés mirando">
      Si programas ocho vídeos y tu saldo da para cuatro, los últimos fallarán por falta de saldo.
      Revisa que el calendario cabe en tu plan antes de llenarlo.
    </Callout>

    <H2 id="ritmo">Qué ritmo tiene sentido</H2>
    <P>
      Más vale uno bueno a la semana que cuatro sin revisar. El calendario produce, pero la revisión
      sigue siendo tuya, y un canal se hunde antes por vídeos malos que por publicar poco.
    </P>
  </>
);

export const Voz = () => (
  <>
    <P>
      La voz es, después del título, lo que más cambia la percepción de un vídeo. Un buen guion con
      una voz que no encaja se abandona a los treinta segundos.
    </P>

    <H2 id="elegir">Elegir voz</H2>
    <P>
      El catálogo está filtrado por el idioma del canal. Puedes afinar por género y por acento, y
      escuchar una muestra antes de decidir. La que elijas queda como voz por defecto del canal y
      se puede cambiar en cualquier vídeo concreto.
    </P>

    <H2 id="criterio">Qué buscar</H2>
    <UL>
      <LI><B>Ritmo pausado</B> para temas históricos o solemnes; más ágil para listas y curiosidades.</LI>
      <LI><B>Acento coherente con la audiencia.</B> Un acento neutro llega a más gente; uno marcado conecta mejor con su región.</LI>
      <LI><B>Escúchala en frases largas</B>, no en dos palabras: los defectos aparecen en las frases largas.</LI>
    </UL>

    <Callout tipo="truco" titulo="Mantén la misma voz">
      Un canal se reconoce por su voz. Cambiarla cada vídeo rompe la sensación de continuidad, que
      es justo lo que hace que alguien se suscriba en vez de ver un vídeo suelto.
    </Callout>

    <H2 id="coste">Cuánto cuesta</H2>
    <P>
      La narración se cobra por caracteres del guion, así que es proporcional a la duración. Es una
      de las tres partidas grandes de un vídeo, junto con la búsqueda de material y las imágenes
      generadas.
    </P>
  </>
);
